import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Radio, Select, Button, Alert, Space, ConfigProvider } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { get_sw_branches_list } from "../../../store/Admin/UserManagement/UserManagementSlice";

// Same amber as the table header / action buttons (Queries.css), applied to
// radios, branch chips, focus rings and the Create button.
const MODAL_THEME = {
  token: { colorPrimary: "#ffaf00" },
  components: {
    Select: { optionSelectedBg: "#fff4d6" },
  },
};

const CUSTOMER_TYPES = [
  { value: "SA", label: "SA – Sub-Agent" },
  { value: "DP", label: "DP – Direct Party" },
];

// `value` is what the backend expects in the payload's `platform` field.
const PLATFORMS = [
  { value: 0, label: "Hike V2", hint: "For Air customers" },
  { value: 1, label: "CargoDashboard", hint: "For Ocean customers" },
  { value: 2, label: "Both", hint: "Air and Ocean Cargo" },
];

/**
 * Normalizes the swbranches_list response into unique { value, label } options.
 * Accepts either `data` being the array or `data.data` being the array, and
 * drops rows without a branch_no.
 */
function toBranchOptions(payload) {
  const body = payload?.data;
  const rows = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
  const seen = new Set();
  const options = [];
  rows.forEach((row) => {
    const no = row?.branch_no;
    if (no === undefined || no === null || no === "" || seen.has(String(no))) return;
    seen.add(String(no));
    options.push({ value: no, label: row?.branch_name?.trim() || `Branch ${no}` });
  });
  return options;
}

/**
 * Collects customer type, platform and branch(es) for a user, then hands the
 * values to `onSubmit`. The branch list is fetched once, on first open.
 */
function CreateCustomerModal({ open, user, submitting, onCancel, onSubmit }) {
  const dispatch = useDispatch();

  const [customerType, setCustomerType] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [branchNos, setBranchNos] = useState([]);
  const [touched, setTouched] = useState(false);
  // Which dropdown is open, so its arrow can flip up/down.
  const [openSelect, setOpenSelect] = useState(null);

  const arrowFor = (name) =>
    openSelect === name ? <UpOutlined style={{ color: "#8c8c8c" }} /> : <DownOutlined style={{ color: "#8c8c8c" }} />;
  const trackOpen = (name) => (isOpen) =>
    setOpenSelect((cur) => (isOpen ? name : cur === name ? null : cur));

  const [branchOptions, setBranchOptions] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchesError, setBranchesError] = useState(null);
  const [branchesLoaded, setBranchesLoaded] = useState(false);

  const loadBranches = () => {
    setBranchesLoading(true);
    setBranchesError(null);
    dispatch(get_sw_branches_list())
      .then((res) => {
        const status = res?.payload?.status;
        if (status >= 200 && status < 300) {
          setBranchOptions(toBranchOptions(res.payload));
          setBranchesLoaded(true);
        } else {
          setBranchesError(res?.payload?.data?.message || "Failed to load branches");
        }
      })
      .catch(() => setBranchesError("Failed to load branches"))
      .finally(() => setBranchesLoading(false));
  };

  useEffect(() => {
    if (open && !branchesLoaded && !branchesLoading) loadBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fresh form every time the modal opens for a user.
  useEffect(() => {
    if (open) {
      setCustomerType(null);
      setPlatform(null);
      setBranchNos([]);
      setTouched(false);
    }
  }, [open, user?._id]);

  const errors = {
    customerType: !customerType && "Please select a customer type",
    platform: platform === null && "Please select a platform",
    branches: branchNos.length === 0 && "Please select at least one branch",
  };
  const isValid = !errors.customerType && !errors.platform && !errors.branches;

  const handleCreate = () => {
    setTouched(true);
    if (!isValid || submitting) return;
    const platformName = PLATFORMS.find((p) => p.value === platform)?.label;
    onSubmit({ customer_type: customerType, platform, platform_name: platformName, branch_no: branchNos });
  };

  const fieldError = (msg) =>
    touched && msg ? <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>{msg}</div> : null;

  return (
    <ConfigProvider theme={MODAL_THEME}>
    <Modal
      open={open}
      title={`Create User${user?.full_name ? ` – ${user.full_name}` : ""}`}
      onCancel={submitting ? undefined : onCancel}
      closable={!submitting}
      maskClosable={!submitting}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="create"
          type="primary"
          loading={submitting}
          disabled={touched && !isValid}
          onClick={handleCreate}
          // Tailwind's preflight resets button backgrounds, which leaves
          // .ant-btn-primary white-on-white — set the fill explicitly.
          style={
            touched && !isValid
              ? { background: "#f5f5f5", borderColor: "#d9d9d9", color: "rgba(0,0,0,0.25)" }
              : { background: "#ffaf00", borderColor: "#ffaf00", color: "#fff" }
          }
        >
          Create
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Select the Customer Type:</div>
        <Radio.Group value={customerType} onChange={(e) => setCustomerType(e.target.value)} disabled={submitting}>
          <Space direction="vertical">
            {CUSTOMER_TYPES.map((t) => (
              <Radio key={t.value} value={t.value}>{t.label}</Radio>
            ))}
          </Space>
        </Radio.Group>
        {fieldError(errors.customerType)}
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Select the required customer creation platform:</div>
        <Select
          placeholder="Select platform"
          suffixIcon={arrowFor("platform")}
          onDropdownVisibleChange={trackOpen("platform")}
          style={{ width: "100%" }}
          value={platform ?? undefined}
          onChange={setPlatform}
          disabled={submitting}
          optionLabelProp="label"
          getPopupContainer={(trigger) => trigger.parentNode}
        >
          {PLATFORMS.map((p) => (
            <Select.Option key={p.value} value={p.value} label={p.label}>
              <strong>{p.label}</strong> <span style={{ color: "#8c8c8c" }}>→ {p.hint}</span>
            </Select.Option>
          ))}
        </Select>
        {fieldError(errors.platform)}
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Select Branch(s):</div>
        {branchesError ? (
          <Alert
            type="error"
            showIcon
            message={branchesError}
            action={<Button size="small" onClick={loadBranches}>Retry</Button>}
          />
        ) : (
          <Select
            mode="multiple"
            showArrow
            suffixIcon={arrowFor("branches")}
            onDropdownVisibleChange={trackOpen("branches")}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={branchesLoading ? "Loading branches..." : "Select branch(s)"}
            style={{ width: "100%" }}
            value={branchNos}
            onChange={setBranchNos}
            options={branchOptions}
            loading={branchesLoading}
            disabled={submitting || branchesLoading}
            notFoundContent={branchesLoading ? "Loading..." : "No branches found"}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        )}
        {fieldError(errors.branches)}
      </div>
    </Modal>
    </ConfigProvider>
  );
}

export default CreateCustomerModal;
